import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { ClientScoreResultsTileContainer } from 'containers/Dashboard/ClientScoreResultsTile';
import { ClientScoresTileComponent } from 'components/Dashboard/ClientScoresTile';
import { ContractorAdministratorGuard } from '../../containers/PaymentConsent/ContractorAdminGuard';
import { GridContainer } from '@pec/aion-ui-components/components/GridContainer';
import { HasPermissionContainer } from '@pec/aion-ui-components/containers/HasPermission';
import { InsuranceExpiredContainer } from 'containers/Dashboard/InsuranceExpired';
import { InsuranceExpiringContainer } from 'containers/Dashboard/InsuranceExpiring';
import { InsuranceV2Container } from 'containers/Dashboard/InsuranceV2Container';
import { isOrganizationProfileLoaded } from 'helpers/helpers';
import { Loading } from '@pec/aion-ui-components/components/Loading';
import { OfferingsTile } from './OfferingsTile';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { PaymentConsentContainer } from '../../containers/PaymentConsent/PaymentConsent';
import { Questionnaire } from './Questionnaire';
import { QuestionnaireTileContainer } from 'containers/Dashboard/QuestionnaireTile';
import { ReleasesContainer } from 'containers/Dashboard/Releases';
import { SubscriptionsExpiredContainer } from '../../containers/Dashboard/SubscriptionsExpired';
import { SubscriptionsExpiringContainer } from '../../containers/Dashboard/SubscriptionsExpiring';
import { Tile } from 'components/Tile';
import { ToDoList } from 'components/NewDashboard/ToDoList';
import { Typography } from '@material-ui/core';
import { UpgradeTile } from '../../containers/Upgrade/UpgradeTile';
import { useFeature } from 'flagged';
import { useOrganizationProfileNotifications } from '@pec/aion-ui-components/hooks/useOrganizationProfileNotifications';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../combineReducers';
import { VerificationsExpiredContainer } from 'containers/Dashboard/VerificationsExpired';
import { VerificationsExpiringContainer } from 'containers/Dashboard/VerificationsExpiring';
import { VerificationsTileContainer } from 'containers/Dashboard/VerificationsTile';

const { Manage, Read } = ActivityAction;
const { Organizations, ContractorsQuestionnaire } = ActivityResourceName;

export const DashboardContainer: React.FC = () => {
  const { organizationProfile } = useTypedSelector(state => state.notifications);
  const { organization } = useTypedSelector(state => state.organization);
  const { isProfileCompleted } = useOrganizationProfileNotifications({ checkManageHiringClientConnections: false });
  const { featureFlags } = useTypedSelector(state => state.featureFlags);
  const { t } = useTranslation('subscriber');
  const isShowToDoListEnabled = useFeature('AionShowToDoListOnSubscriberUI');

  const isNgqeUsCohortLaunchInProgress = () => {
    let feature = featureFlags.find(x => x.Key === 'FeatureManagement:NgqeUsCohort');
    let isEnabled = atob(feature?.Value || '');
    return isEnabled === 'true';
  };

  return (
    <HasPermissionContainer>
      {({ hasOrganizationPermission, hasGlobalPermission }) => {
        const hasOrganisationAccess =
          hasGlobalPermission(Read, Organizations) || hasOrganizationPermission(Read, Organizations);
        const isAdmin = hasGlobalPermission(Manage, Organizations);
        const isProfileComplete = hasOrganisationAccess && isProfileCompleted;
        const isProfileLoaded = hasOrganisationAccess && isOrganizationProfileLoaded(organizationProfile);
        const isLoading = !isProfileLoaded;
        const isOrganizationLoaded = !!organization;
        const hasQuestionnaireV2OrgFeature = Boolean(
          organization?.features.includes(OrganizationFeature.QuestionnaireV2)
        );
        const hasOSHAViolationsIntegrationFeature = Boolean(
          organization?.features.includes(OrganizationFeature.QuestionnaireOSHAViolationIntegration)
        );

        return (
          <GridContainer spacing={0}>
            <Grid item xs={12}>
              <GridContainer>
                <SubscriptionsExpiredContainer />
                <SubscriptionsExpiringContainer />
                <VerificationsExpiredContainer />
                <VerificationsExpiringContainer />
                {hasQuestionnaireV2OrgFeature ? (
                  <InsuranceV2Container />
                ) : (
                  <>
                    <InsuranceExpiredContainer />
                    <InsuranceExpiringContainer />
                  </>
                )}
                <ReleasesContainer />
                <ContractorAdministratorGuard>
                  <PaymentConsentContainer />
                </ContractorAdministratorGuard>
              </GridContainer>
            </Grid>
            {!isLoading && (!hasOrganisationAccess || isProfileComplete || isAdmin) && (
              <>
                <Grid container justify="flex-end">
                  <Grid item>
                    <UpgradeTile />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <GridContainer>
                    {isShowToDoListEnabled && (
                      <Grid item xs={12}>
                        <ToDoList />
                      </Grid>
                    )}
                    {isNgqeUsCohortLaunchInProgress() && (
                      <Grid item xs={12}>
                        <Tile primaryText={t('dashboard.questionnaireProgress.title', 'Questionnaire')}>
                          <GridContainer>
                            <Typography variant="h6" color="error">
                              Your questionnaire is temporarily unavailable as we work on enhancing your questionnaire
                              experience. You will be notified once this upgrade is complete.
                            </Typography>
                          </GridContainer>
                        </Tile>
                      </Grid>
                    )}
                    {hasQuestionnaireV2OrgFeature &&
                      !isNgqeUsCohortLaunchInProgress() &&
                      (hasGlobalPermission(Read, ContractorsQuestionnaire) ||
                        hasOrganizationPermission(Read, ContractorsQuestionnaire)) && (
                        <Grid item xs={12}>
                          <Questionnaire />
                        </Grid>
                      )}
                    {!hasQuestionnaireV2OrgFeature && !isNgqeUsCohortLaunchInProgress() && (
                      <Grid item xs={12}>
                        <QuestionnaireTileContainer />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <OfferingsTile />
                    </Grid>
                  </GridContainer>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <GridContainer>
                    <Grid item xs={12}>
                      {hasQuestionnaireV2OrgFeature ? (
                        <ClientScoreResultsTileContainer />
                      ) : (
                        <ClientScoresTileComponent />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {isOrganizationLoaded && (
                        <VerificationsTileContainer
                          hasOSHAViolationsIntegrationFeature={hasOSHAViolationsIntegrationFeature}
                        />
                      )}
                    </Grid>
                  </GridContainer>
                </Grid>
              </>
            )}
            {isLoading && <Loading />}
          </GridContainer>
        );
      }}
    </HasPermissionContainer>
  );
};
